/**
 * Bill activity timeline — `prototype/purchases/bill-detail.html` ▸
 * `.timeline-card`. Renders events derivable from the bill row (capture
 * + status). Inlines an SVG inside each `.timeline-marker` matching the
 * state variant (`.s-received`, `.s-extracted`, etc.) — empty markers
 * are not acceptable per playbook §6.4. Full activity feed lands in V1.x.
 */
import { Check, FileText, Inbox, ShieldCheck, Sparkles } from "lucide-react";
import { formatDateTime } from "../../../lib/format.ts";
import type { BillStatus } from "../list/index.ts";
import { statusLabel } from "./bill-detail-header.tsx";

/** Minimal bill activity data derivable from the bill row. */
export interface BillTimelineProps {
  data: {
    createdAt: string;
    source: "photo" | "email_pdf" | "manual";
    status: BillStatus;
    threeWayMatch: "matched" | "mismatch" | "na";
  };
}

/** Render the derived bill timeline until `billActivityFeed` lands. */
export function BillTimeline({ data }: BillTimelineProps) {
  const events = derivedEvents(data);
  return (
    <section className="timeline-card" aria-label="Activity timeline">
      <div className="head">
        <h3>Activity</h3>
        <p className="sub">Captured timeline — full audit feed lands in V1.x</p>
      </div>
      <ol className="timeline">
        {events.map((event, i) => (
          <li key={`${event.kind}-${i}`} className={`timeline-item s-${event.kind}`}>
            <span className="timeline-marker">{markerIcon(event.kind)}</span>
            <div className="timeline-content">
              <div className="head">
                <span className="what">{event.what}</span>
                <span className="when">{event.when}</span>
              </div>
              <div className="body">{event.body}</div>
            </div>
          </li>
        ))}
      </ol>
    </section>
  );
}

type EventKind = "received" | "extracted" | "matched" | "approval";

interface DerivedEvent {
  kind: EventKind;
  what: string;
  when: string;
  body: string;
}

function derivedEvents({
  createdAt,
  source,
  status,
  threeWayMatch,
}: BillTimelineProps["data"]): DerivedEvent[] {
  const events: DerivedEvent[] = [
    {
      kind: "received",
      what: "Bill captured",
      when: formatDateTime(createdAt),
      body: `Source: ${sourceLabel(source)}.`,
    },
  ];
  if (source !== "manual" && status !== "draft") {
    events.push({
      kind: "extracted",
      what: "Fields extracted by AI",
      when: "Shortly after capture",
      body: "Vendor + amounts auto-filled. Operator confirmed line items before submit.",
    });
  }
  if (
    threeWayMatch === "matched" &&
    ["review", "approval_pending", "ready_to_pay", "paid"].includes(status)
  ) {
    events.push({
      kind: "matched",
      what: "3-way match passed",
      when: "On submit",
      body: "PO ↔ GRN ↔ bill reconcile (heuristic until PO + GRN entities land).",
    });
  }
  if (["approval_pending", "ready_to_pay", "paid"].includes(status)) {
    events.push({
      kind: "approval",
      what:
        status === "paid" || status === "ready_to_pay"
          ? "Approval recorded"
          : "Routed for approval",
      when:
        status === "paid" || status === "ready_to_pay"
          ? "After founder OTP"
          : "Awaiting founder OTP",
      body: `Current status: ${statusLabel(status)}.`,
    });
  }
  return events;
}

function markerIcon(kind: EventKind) {
  // size is explicit so the marker container's 20×20 box doesn't get
  // blown out by Lucide's default 24×24.
  if (kind === "received") return <Inbox size={10} strokeWidth={2.4} aria-hidden />;
  if (kind === "extracted") return <Sparkles size={10} strokeWidth={2.4} aria-hidden />;
  if (kind === "matched") return <Check size={10} strokeWidth={2.6} aria-hidden />;
  if (kind === "approval") return <ShieldCheck size={10} strokeWidth={2.4} aria-hidden />;
  return <FileText size={10} strokeWidth={2.4} aria-hidden />;
}

function sourceLabel(source: "photo" | "email_pdf" | "manual"): string {
  if (source === "photo") return "Photo upload";
  if (source === "email_pdf") return "Email PDF";
  return "Manual entry";
}
